import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import axios from 'axios';

// Async thunk for fetching products
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('https://evercorenutrition.com/ev_admin/api/productslist/');
      const data = response.data;

      // API returns { count, next, previous, results: [...] }
      if (Array.isArray(data.results)) return data.results;
      if (Array.isArray(data)) return data;
      if (Array.isArray(data.products)) return data.products;
      if (Array.isArray(data.data)) return data.data;

      console.warn('Unexpected API response shape:', data);
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const productsSlice = createSlice({
  name: 'products',
  initialState: {
    items: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    selectedProduct: null,
    filters: {
      flavor: [],
      priceRange: { min: 0, max: 100000 },
      discount: [],
      packSize: []
    }
  },
  reducers: {
    setSelectedProduct: (state, action) => {
      state.selectedProduct = action.payload;
    },
    clearSelectedProduct: (state) => {
      state.selectedProduct = null;
    },
    setFlavorFilter: (state, action) => {
      state.filters.flavor = action.payload;
    },
    setPriceRange: (state, action) => {
      state.filters.priceRange = action.payload;
    },
    setDiscountFilter: (state, action) => {
      state.filters.discount = action.payload;
    },
    setPackSizeFilter: (state, action) => {
      state.filters.packSize = action.payload;
    },
    clearAllFilters: (state) => {
      state.filters = {
        flavor: [],
        priceRange: { min: 0, max: 100000 },
        discount: [],
        packSize: []
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  }
});

// Export actions
export const {
  setSelectedProduct,
  clearSelectedProduct,
  setFlavorFilter,
  setPriceRange,
  setDiscountFilter,
  setPackSizeFilter,
  clearAllFilters
} = productsSlice.actions;

// Base selectors
export const selectAllProducts = (state) => state.products.items;
export const selectProductsStatus = (state) => state.products.status;
export const selectProductsError = (state) => state.products.error;
export const selectSelectedProduct = (state) => state.products.selectedProduct;
export const selectProductFilters = (state) => state.products.filters;

// Helper: extract numeric value from price string or number
const extractPrice = (val) => {
  if (val === null || val === undefined) return 0;
  const numeric = parseFloat(String(val).replace(/[^0-9.-]+/g, ''));
  return isNaN(numeric) ? 0 : numeric;
};

// Helper: get the main image URL for a product
// API provides: main_image (relative path) or images[] array with full URLs
export const getProductImage = (product) => {
  if (!product) return '/assets/images/placeholder.jpg';

  // Prefer full-URL image from images array where is_main = true
  if (Array.isArray(product.images) && product.images.length > 0) {
    const mainImg = product.images.find(img => img.is_main);
    if (mainImg?.image) return mainImg.image;
    // fallback to first image full URL
    if (product.images[0]?.image) return product.images[0].image;
  }

  // Fallback to main_image relative path (prepend base URL)
  if (product.main_image) {
    return `https://evercorenutrition.com${product.main_image}`;
  }

  return '/assets/images/placeholder.jpg';
};

// Helper: get selling price (discounted price)
export const getSellingPrice = (product) => {
  return extractPrice(product?.selling_price || product?.price || 0);
};

// Helper: get original MRP
export const getOriginalPrice = (product) => {
  return extractPrice(product?.price || 0);
};

// Helper: get pack size string e.g. "100g", "1kg"
export const getPackSize = (product) => {
  if (!product) return '';
  const val = product.weight_value ? parseFloat(product.weight_value) : '';
  const unit = product.weight_unit || '';
  return val !== '' && unit ? `${val}${unit}` : '';
};

// Memoized selector for filtered products
// Memoized selector for filtered products
export const selectFilteredProducts = createSelector(
  [selectAllProducts, selectProductFilters],
  (products, filters) => {
    if (!Array.isArray(products) || products.length === 0) return [];

    return products.filter(product => {
      // Check if product has variants
      const hasVariants = product.variants && product.variants.length > 0;

      // FILTER BY FLAVOR - check variants
      if (filters.flavor.length > 0) {
        // If product has variants, check if any variant matches the selected flavors
        if (hasVariants) {
          const hasMatchingFlavor = product.variants.some(variant => 
            variant.flavour && filters.flavor.includes(variant.flavour)
          );
          if (!hasMatchingFlavor) return false;
        } else {
          // If no variants, fall back to checking product name/tags
          const searchTarget = `${product.tags || ''} ${product.name || ''}`.toLowerCase();
          if (!filters.flavor.some(f => searchTarget.includes(f.toLowerCase()))) {
            return false;
          }
        }
      }

      // FILTER BY PACK SIZE - check variants
      if (filters.packSize.length > 0) {
        if (hasVariants) {
          const hasMatchingPackSize = product.variants.some(variant => 
            variant.weight_display && filters.packSize.includes(variant.weight_display)
          );
          if (!hasMatchingPackSize) return false;
        } else {
          // If no variants, use product-level weight
          const packSize = getPackSize(product).toLowerCase();
          if (!filters.packSize.some(size => packSize.includes(size.toLowerCase()))) {
            return false;
          }
        }
      }

      // FILTER BY PRICE - use variant prices if available
      const minPrice = hasVariants 
        ? Math.min(...product.variants.map(v => parseFloat(v.price)))
        : getSellingPrice(product);
        
      if (minPrice < filters.priceRange.min || minPrice > filters.priceRange.max) {
        return false;
      }

      // FILTER BY DISCOUNT - check product-level discount
      if (filters.discount.length > 0) {
        const discount = extractPrice(product.discount);

        let discountMatch = false;
        filters.discount.forEach(range => {
          if (range === 'upto5' && discount <= 5) discountMatch = true;
          if (range === '5to10' && discount > 5 && discount <= 10) discountMatch = true;
          if (range === '10to15' && discount > 10 && discount <= 15) discountMatch = true;
          if (range === '15to25' && discount > 15 && discount <= 25) discountMatch = true;
          if (range === 'above25' && discount > 25) discountMatch = true;
        });

        if (!discountMatch) return false;
      }

      return true;
    });
  }
);

// Memoized selector for product counts by category
export const selectProductCountByCategory = createSelector(
  [selectAllProducts],
  (products) => {
    if (!Array.isArray(products)) return {};

    return products.reduce((acc, product) => {
      const category = product.veg_nonveg || 'Uncategorized';
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});
  }
);

export default productsSlice.reducer;